import { createContext, useContext, useEffect, useState } from 'react';
import { Gamepad2, Rocket, Ghost, Swords, Crown, Zap, Flame, Joystick, Sparkles, Dices, Atom, Skull } from 'lucide-react';

export const LOGO_ICONS = { gamepad: Gamepad2, rocket: Rocket, ghost: Ghost, swords: Swords, crown: Crown, zap: Zap, flame: Flame, joystick: Joystick, sparkles: Sparkles, dices: Dices, atom: Atom, skull: Skull };
import { api } from '../lib/api';
import { applyBrand, cachedBrand, DEFAULT_BRAND } from '../lib/brand';

/** @typedef {{enablePublicStore:boolean, enableGameStudio:boolean, enableGlobalLeaderboard:boolean, enableLegalFooter:boolean, enableUserRegistration:boolean, maintenanceMode:boolean, maintenanceNotice:string}} PlatformFeatureFlags */
/** @typedef {{privacyPolicy:string, termsOfService:string, cookieBannerText:string, disclaimerText:string, lastUpdatedDate:string}} PlatformLegalDocs */

export const DEFAULT_FEATURES = {
  enablePublicStore: true, enableGameStudio: true, enableGlobalLeaderboard: true, enableLegalFooter: true,
  enableUserRegistration: true, maintenanceMode: false, maintenanceNotice: 'Stiamo aggiornando la piattaforma: torna tra poco!',
};
export const DEFAULT_LEGAL = { privacyPolicy: '', termsOfService: '', cookiePolicy: '', legalNotice: '', footerContacts: '', cookieBannerText: '', disclaimerText: '', lastUpdatedDate: '' };
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const read = (k, d) => { try { return { ...d, ...(JSON.parse(localStorage.getItem(k)) || {}) }; } catch { return d; } };
const Ctx = createContext(null);
export const useBrand = () => useContext(Ctx);
export const usePlatform = () => useContext(Ctx);

function setFavicon(url) {
  document.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]').forEach((l) => { l.href = url; });
}

export function BrandProvider({ children }) {
  const [brand, setState] = useState(cachedBrand);
  const [features, setF] = useState(() => read('nexora.features', DEFAULT_FEATURES));
  const [legal, setL] = useState(() => read('nexora.legal', DEFAULT_LEGAL));
  const logoUrl = brand.logoVersion ? `${API}/platform/logo?v=${brand.logoVersion}` : null;

  const setBrand = (b) => {
    const n = { ...DEFAULT_BRAND, ...b };
    setState(n);
    applyBrand(n);
    localStorage.setItem('nexora.brand', JSON.stringify(n));
  };
  const setFeatures = (f) => { setF(f); localStorage.setItem('nexora.features', JSON.stringify(f)); };
  const setLegal = (l) => { setL(l); localStorage.setItem('nexora.legal', JSON.stringify(l)); };

  useEffect(() => {
    applyBrand(brand);
    api('/brand', { timeout: 8000 }).then(setBrand).catch(() => {});
    api('/platform', { timeout: 8000 }).then((p) => { setFeatures(p.features); setLegal(p.legal); }).catch(() => {});
  }, []); // eslint-disable-line
  useEffect(() => { setFavicon(logoUrl || '/favicon.ico'); }, [logoUrl]);

  return <Ctx.Provider value={{ brand, setBrand, logoUrl, features, setFeatures, legal, setLegal }}>{children}</Ctx.Provider>;
}

export function BrandMark({ className = 'h-6 w-6' }) {
  const { logoUrl, brand } = useBrand();
  const Icon = LOGO_ICONS[brand.logoIcon];
  if (!logoUrl && Icon) return <Icon data-testid="brand-logo-icon" className={`${className} text-violet-400`} strokeWidth={1.8} />;
  if (logoUrl) return <img data-testid="brand-logo" src={logoUrl} alt="" className={`${className} rounded-md object-contain`} />;
  return (
    <svg viewBox="0 0 24 24" className={`${className} text-violet-400`} fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /></svg>
  );
}
